import {useCallback, useState, useEffect, useMemo} from 'react'
import {DragDropProvider} from '@dnd-kit/react';
import {useSortable} from '@dnd-kit/react/sortable';
import {arrayMove} from '@dnd-kit/helpers';

import { ActionLink, PhotoGrid, MorphBox } from 'app/_shared'
import safeLocalStorage from 'app/_shared/safeLocalStorage'
import { useListing } from 'app/listing'
import { updatePhotos } from '../../service'

import Photo from './Photo'

import styles from './index.module.scss'

const SortablePhoto = ({photo, index, onToggle}) => {
  const {ref, handleRef} = useSortable({id: photo.code, index});
  return (
    <Photo
      ref={ref}
      handleRef={handleRef}
      photo={photo}
      onToggle={onToggle}
      style={photo.grid}
    />
  )
}

const store = (tourName, photos) => safeLocalStorage.setItem(`photoEditState_${tourName}`, JSON.stringify(photos.map(t => ({ code: t.code, hidden: t.hidden }))));
const removeStored = (tourName, photos) => {
  safeLocalStorage.removeItem(`photoEditState_${tourName}`);
  return photos.map(t => ({ ...t }));
}
const getStored = (tourName, photos) => {
  const stored = JSON.parse(safeLocalStorage.getItem(`photoEditState_${tourName}`));
  if (Array.isArray(stored) && stored.length === photos.length && photos.every(t => stored.find(s => s.code === t.code))) {
    return stored.map(t => ({...photos.find(s => s.code === t.code), hidden: t.hidden }));
  }
  else {
    return removeStored(tourName, photos);
  }
}

export default ({ updateListingPhotos }) => {
  const { listing: { tourName, photos }} = useListing();
  const [photoState, setPhotoState] = useState();
  const [saving, setSaving] = useState();

  useEffect(() => setPhotoState(getStored(tourName, photos)), [tourName, photos]);

  const hasChanges = useMemo(() => (Array.isArray(photoState) &&
    (photoState.length !== photos.length || photoState.some((t, i) => photos[i].code !== t.code || photos[i].hidden !== t.hidden))
  ), [photoState, photos]);

  const onSortEnd = useCallback(({operation: {source: {initialIndex, index}}}) => {
    if (initialIndex !== index) {
      setPhotoState(state => {
        const newState = arrayMove(state, initialIndex, index);
        store(tourName, newState);
        // write new state to storage
        return newState;
      });
    }
  }, [tourName]);

  const updateVisibility = useCallback((index, visible) => {
    setPhotoState(state => {
      const newState = [...state];
      newState[index].hidden = !visible;
      store(tourName, newState);
      return newState;
    });
  }, [tourName]);

  const discardChanges = useCallback(() => setPhotoState(removeStored(tourName, photos)), [photos, tourName]);

  const saveChanges = useCallback(hasChanges && !saving ? async () => {
    setSaving(true);
    let newPhotos = await updatePhotos(tourName, getStored(tourName, photos));
    updateListingPhotos(tourName, newPhotos);
    setSaving(false);
  } : undefined, [photos, tourName, hasChanges]);

  return (
    <div className={styles.admin}>
      { photoState &&
        <DragDropProvider onDragEnd={onSortEnd}>
          <PhotoGrid photos={photoState} className={styles.photos} maxRowHeight={200} render={layout => layout.map((t, i) => (
            <SortablePhoto key={t.code} index={i} photo={t} onToggle={visible => updateVisibility(i, visible)} />
          ))} />
        </DragDropProvider>
      }
      <MorphBox className={styles.save}>
        { (hasChanges || saving) &&
          <div className={styles.inner}>
            <ActionLink appearance="plain button" onClick={discardChanges} disabled={saving}>
              Discard changes
            </ActionLink>
            <ActionLink appearance="button" onClick={saveChanges} disabled={saving}>
              Save
            </ActionLink>
          </div>
        }
      </MorphBox>
    </div>
  )
}
